import { Component, inject } from '@angular/core';
import { HlAreaComponent } from './hl-area.component';
import { HorizontZeile, isG, isM, neueZeilenKennung, roundToString, roundToNumber, trackByIndex, isTorf, isMudde, isQuellsediment } from 'src/app/data-model';
import { StorageService, ConfirmService } from '@luzula/luzula-lib';
import { DataService } from 'src/app/data.service';
import { ensureSubstratRow, istBindigSubstrat } from './grube-profil-helpers';
import { AUFLAGE_ZEILEN, AUFLAGE_FELDNAMEN, AUFLAGE_UEBERSCHRIFT, GELAENDEOBERFLAECHE_TEXT, auflagePlatzhalterText } from './humus-auflage';
import { gelaendeoberflaecheZeile } from '../../filz-tiefen';
import { SpalteGilt, StufenFeld, fuelleNachUnten, hatZeilenDarunter } from './substrat-nach-unten';
import { WechselLage, wechselLage } from '../wechsel-gruppen';
import { HUMUS_STUFEN_FELD } from '../../help/humus-stufen';
import { LAGERUNGSDICHTE_STUFEN_FELD } from '../../help/lagerungsdichte-stufen';
import { SKELETT_STUFEN_FELD } from '../../help/skelett-stufen';
import { KARBONAT_STUFEN_FELD } from '../../help/karbonat-stufen';
import { ZERSETZUNGSGRAD_STUFEN_FELD } from '../../help/zersetzungsgrad-stufen';
import { MUDDE_KONSISTENZ_STUFEN_FELD } from '../../help/mudde-konsistenz-stufen';
import { SUBSTANZVOLUMEN_STUFEN_FELD } from '../../help/substanzvolumen-stufen';
import { FESTIGKEIT_STUFEN_FELD } from '../../help/festigkeit-stufen';
import { FEINDURCHWURZELUNG_HAEUFUNG_FELD } from '../../help/feindurchwurzelung-stufen';

@Component({
    standalone: false,
    selector: 'bp-substrate-horizonte',
    templateUrl: './substrate-horizonte.component.html',
    styleUrls: ['./hl-area.component.css', '../bohrpunkt-boden/bohrpunkt-boden.component.css'],
    
})
export class SubstrateHorizonteComponent extends HlAreaComponent {
	trackByIndex = trackByIndex;
	private storage = inject(StorageService);
	private ds = inject(DataService);
	private confirmService = inject(ConfirmService);

	private readonly STORAGE_KEY_SUBSTRAT = 'recentSubstrate';
	private readonly STORAGE_KEY_HORIZONT = 'recentHorizont';
	private readonly MAX_HISTORY = 100;
	private readonly MAX_SUGGESTIONS = 8;

	recentSubstrate: string[] = [];
	recentHorizonte: string[] = [];
	private trackingEnabled = false;

	// Humusauflage: drei feste Zeilen oben in der Horizontfolge (siehe humus-auflage.ts)
	auflageZeilen = AUFLAGE_ZEILEN;
	auflageFeldNamen = AUFLAGE_FELDNAMEN;
	auflageUeberschrift = AUFLAGE_UEBERSCHRIFT;
	// Platzhalter der Substratseite samt Gesamt-Auflage („organische Auflage · 6 cm")
	auflagePlatzhalterText = auflagePlatzhalterText;
	gelaendeoberflaecheTitel = GELAENDEOBERFLAECHE_TEXT;
	// Horizontseite: mit Gesamt-Auflage, bei einem Filz mit dem Hinweis, dass
	// trotzdem ab Mineralboden eingegeben wird (filz-tiefen.ts).
	gelaendeoberflaecheZeile = gelaendeoberflaecheZeile;

	// Farben und Namen der Stufen-Spalten — dieselben Reihen wie in den
	// Hilfefenstern und -texten, aus den *-stufen.ts-Dateien. Eine Quelle.
	// Wo das Hilfefenster keine Legende fuehrt (Konsistenz, Substanzvolumen,
	// Festigkeit, Fdwz-Haeufung), gilt die graue Treppe (stufen-farbtreppe.ts).
	humusStufen = HUMUS_STUFEN_FELD;
	lagerungsdichteStufen = LAGERUNGSDICHTE_STUFEN_FELD;
	skelettStufen = SKELETT_STUFEN_FELD;
	karbonatStufen = KARBONAT_STUFEN_FELD;
	zersetzungsgradStufen = ZERSETZUNGSGRAD_STUFEN_FELD;
	konsistenzStufen = MUDDE_KONSISTENZ_STUFEN_FELD;
	substanzvolumenStufen = SUBSTANZVOLUMEN_STUFEN_FELD;
	festigkeitStufen = FESTIGKEIT_STUFEN_FELD;
	haeufungStufen = FEINDURCHWURZELUNG_HAEUFUNG_FELD;

	// Moorprotokoll: Zersetzungsgrad nur bei Torf, Konsistenz nur bei Mudde
	// (Quellsediment beides) — dieselbe Regel wie die *ngIf in der Vorlage.
	// „Nach unten" ueberspringt damit die Zeilen, die das Feld gar nicht zeigen.
	giltZersetzungsgrad: SpalteGilt = z => isTorf(z) || isQuellsediment(z);
	giltKonsistenz: SpalteGilt = z => isMudde(z) || isQuellsediment(z);

	fieldNameMap: Record<string, string> = {
		'STiefe': 'Tiefe',
		'SMächtigkeit': 'Mächtigkeit',
		'Substrat': 'Substrat',
		'Wechselschichtung': 'Wechsel',
		'Humusgehalt': 'Humus',
		'Lagerungsdichte': 'Lager.',
		'Skelettgehalt': 'Skelett',
		'Karbonat': 'Karbonat',
		'Torfart1': 'Torfart 1',
		'Torfart2': 'Torfart 2',
		'Torfart3': 'Torfart 3',
		'Torf-Zersetzungsgrad': 'Zersetz.',
		'Mudde-Konsistenz': 'Konsist.',
		'Substanzvolumen': 'Subst.vol.',
		'Besonderheiten': 'Besond.',
		'Farbe': 'Farbe',
		'Substratherkunft': 'Substr.herk.',
		'Perstruktionszone': 'Perstr.zone',
		'Festigkeit': 'Festigk.',
		'GefuegeForm': 'Gef. Form',
		'GefuegeGroesse': 'Gef. Größe',
		'GefuegeAuspraegung': 'Gef. Auspr.',
		'FeindurchwurzelungDichte': 'Fdwz Dichte',
		'FeindurchwurzelungHaeufung': 'Fdwz Häuf.',
		'ProbeSpanne': 'Probe Span.',
		'ProbeFlaeche': 'Probe Fläche',
		'ProbeVolumen': 'Probe Vol.',
		'HTiefe': 'Tiefe',
		'HMächtigkeit': 'Mächtigkeit',
		'Horizont1': 'Horizont 1',
		'Horizont2': 'Horizont 2',
		'Horizont3': 'Horizont 3'
	};
	
	roundToString = roundToString;
	roundToNumber = roundToNumber;
	isTorf = isTorf;
	isMudde = isMudde;
	isQuellsediment = isQuellsediment;
	istBindig = istBindigSubstrat;

  onAddSubstratZeile(i = -1) {
		const l = this.data.Substratfolge.length;
		const cp: any = { id: neueZeilenKennung(), torfarten: ['', '', ''] };
		if (isG(this.data)) ensureSubstratRow(cp);
		if (i === -1) this.data.Substratfolge.push(cp);
		else this.data.Substratfolge.splice(i, 0, cp);
		this.vs.dirty.next(true);
  }

  async onRemoveSubstratZeile(i: number) {
		const tiefe = this.data.Substratfolge[i].tiefe;
		const label = tiefe ? `Substratzeile (bis ${tiefe} cm)` : 'Substratzeile';
		if (!(await this.confirmService.confirmDelete(`${label} wirklich löschen?`))) return;
		const suffix = `(${tiefe}cm)`;
		// clearCommentsNoSave verwenden um mehrfaches Speichern zu vermeiden
		// Das Speichern erfolgt erst wenn der User explizit speichert (dirty=true)
		this.vs.clearCommentsNoSave(`STiefe[${i}]`);
		const fields = [
			'SMächtigkeit', 'Substrat', 'Wechselschichtung',
			'Humusgehalt', 'Lagerungsdichte', 'Skelettgehalt', 'Karbonat', 'nWSK',
			'Torfart1', 'Torfart2', 'Torfart3', 'Zersetzungsgrad', 'Zersetzungsgrad Mudde',
			'Substanzvolumen', 'Farbe',
			// G-Felder
			'Substratherkunft', 'Perstruktionszone', 'Festigkeit',
			'GefuegeForm', 'GefuegeGroesse', 'GefuegeAuspraegung',
			'FeindurchwurzelungDichte', 'FeindurchwurzelungHaeufung',
			'Besonderheiten', 'ProbeSpanne', 'ProbeFlaeche', 'ProbeVolumen'
		];
		for (const f of fields) this.vs.clearCommentsNoSave(`${f}[${i}]${suffix}`);

		this.data.Substratfolge.splice(i, 1);
		this.vs.dirty.next(true);
  }

	/**
	 * Wo steht Zeile i in ihrer Wechsel-Kette? Der Haken heisst „wechselt mit
	 * der Zeile darunter"; die Klammer neben der Spalte zeigt, welche Zeilen
	 * damit zusammengehoeren. Regel in wechsel-gruppen.ts — dieselbe, die die
	 * Uebersicht und das gedruckte Protokoll benutzen.
	 */
	wechselLage(i: number): WechselLage {
		return wechselLage(this.data?.Substratfolge, i);
	}

	/**
	 * Gibt es unter Zeile i noch Substratzeilen, in denen die Spalte gilt? Nur
	 * dann bietet das Stufen-Feld „nach unten" an.
	 */
	hatZeilenDarunter(i: number, gilt?: SpalteGilt): boolean {
		return hatZeilenDarunter(this.data?.Substratfolge, i, gilt);
	}

	/**
	 * Der Wert von Zeile i geht in alle Substratzeilen darunter, in denen die
	 * Spalte gilt — auch in die, die schon einen anderen Wert haben
	 * (Nutzer-Wunsch 15.09.2026: „nach unten alles ausfüllen, wie in der
	 * aktuellen Zelle"). Kommt vom langen Druck auf das Feld oder vom Eintrag
	 * am Ende seiner Liste.
	 */
	fuelleNachUnten(i: number, feld: StufenFeld, wert: number, gilt?: SpalteGilt): void {
		if (fuelleNachUnten(this.data?.Substratfolge, i, feld, wert, gilt)) this.vs.dirty.next(true);
	}

	onAddHorizontZeile(i = -1) {
		const l = this.data.Horizontfolge.length;
		// Eine neue Zeile entsteht leer: ohne Tiefe, ohne Maechtigkeit, mit drei
		// leeren Horizont-Plaetzen. Leer heisst '' wie beim Import (`[h, '', '']`),
		// nicht null - der Typ fuehrt Text, und alle Leser behandeln beides gleich.
		const cp: HorizontZeile = { id: neueZeilenKennung(), horizonte: ['', '', ''] };
		if (i === -1) this.data.Horizontfolge.push(cp);
		else this.data.Horizontfolge.splice(i, 0, cp);
		this.vs.dirty.next(true);
	}

	async onRemoveHorizontZeile(i: number) {
		const hf = this.data.Horizontfolge[i];
		const tiefe = hf.tiefe;
		const label = tiefe ? `Horizontzeile (bis ${tiefe} cm)` : 'Horizontzeile';
		if (!(await this.confirmService.confirmDelete(`${label} wirklich löschen?`))) return;
		const hs = hf.horizonte;
		// clearCommentsNoSave verwenden um mehrfaches Speichern zu vermeiden
		this.vs.clearCommentsNoSave(`HTiefe[${i}]`);
		this.vs.clearCommentsNoSave(`HMächtigkeit[${i}](${tiefe}cm)`);
		for (let j = 0; j < hs.length; ++j) {
			this.vs.clearCommentsNoSave(`Horizont${j+1}[${i}](${tiefe}cm)`);
		}

		this.data.Horizontfolge.splice(i, 1);
		this.vs.dirty.next(true);
	}
	
	colSpanSubstrate(): number {
		// B: 11, M: 17 (3 Torfarten + 4), G: 24 (13 Grube-Zusatzspalten — alle Werte je eine Spalte)
		if (isM(this.data)) return 17;
		if (isG(this.data)) return 24;
		return 11;
	}

	/** Feldpräfixe für Substratzeilen-Kommentare */
	substratFieldPrefixes(): string[] {
		const base = ['STiefe', 'SMächtigkeit', 'Substrat', 'Wechselschichtung', 'Skelettgehalt', 'Karbonat'];
		if (isM(this.data)) {
			return [...base, 'Torfart1', 'Torfart2', 'Torfart3', 'Torf-Zersetzungsgrad', 'Mudde-Konsistenz', 'Substanzvolumen', 'Besonderheiten', 'Farbe'];
		}
		if (isG(this.data)) {
			return [
				...base, 'Humusgehalt', 'Lagerungsdichte', 'Festigkeit',
				'Substratherkunft', 'Perstruktionszone', 'Farbe',
				'GefuegeForm', 'GefuegeGroesse', 'GefuegeAuspraegung',
				'FeindurchwurzelungDichte', 'FeindurchwurzelungHaeufung',
				'Besonderheiten', 'ProbeSpanne', 'ProbeFlaeche', 'ProbeVolumen'
			];
		}
		return [...base, 'Humusgehalt', 'Lagerungsdichte'];
	}

	/** Feldpräfixe für Horizontzeilen-Kommentare */
	horizontFieldPrefixes(): string[] {
		return ['HTiefe', 'HMächtigkeit', 'Horizont1', 'Horizont2', 'Horizont3'];
	}

	/**
	 * Offener Kommentar an Ol/Of/Oh? Die drei Felder tragen keinen Zeilenindex,
	 * deshalb laufen sie über `fieldNames` der Kommentar-Zeile, nicht über die
	 * Präfixe der Zeilenlisten.
	 */
	hatAuflageKommentare(): boolean {
		for (const feld of this.auflageFeldNamen) {
			const c = this.vs.hasComments(feld);
			if (c && !c.resolved) return true;
		}
		return false;
	}

	/** Prüft ob eine Substratzeile unaufgelöste Kommentare hat */
	hasSubstratRowComments(i: number, tiefe?: number): boolean {
		const suffix = tiefe !== undefined ? `(${tiefe}cm)` : '';
		for (const prefix of this.substratFieldPrefixes()) {
			// Mit Suffix
			const c1 = this.vs.hasComments(`${prefix}[${i}]${suffix}`);
			if (c1 && !c1.resolved) return true;
			// Ohne Suffix (für STiefe)
			if (suffix) {
				const c2 = this.vs.hasComments(`${prefix}[${i}]`);
				if (c2 && !c2.resolved) return true;
			}
		}
		return false;
	}

	/** Prüft ob eine Horizontzeile unaufgelöste Kommentare hat */
	hasHorizontRowComments(i: number, tiefe?: number): boolean {
		const suffix = tiefe !== undefined ? `(${tiefe}cm)` : '';
		for (const prefix of this.horizontFieldPrefixes()) {
			const c1 = this.vs.hasComments(`${prefix}[${i}]${suffix}`);
			if (c1 && !c1.resolved) return true;
			if (suffix) {
				const c2 = this.vs.hasComments(`${prefix}[${i}]`);
				if (c2 && !c2.resolved) return true;
			}
		}
		return false;
	}
	
	schichtNWSK(i: number): string | undefined {
		const result = this.es.evaluate(`SnWSK[${i}]`) as number | undefined;
		if (result === undefined || result === null || isNaN(result)) return undefined;
		return Number(result).toFixed(2).replace('.', ',');
	}

	schichtNWSKGesamt(): string | undefined {
		// Live berechnen statt gespeicherten Wert lesen
		const v = this.es.evaluate('nWSK') as number | undefined;
		if (v === undefined || v === null || isNaN(v)) return undefined;
		// Wert auch im Dokument aktualisieren für Speicherung
		this.data.extendedHeader.nWsk = v;
		return Number(v).toFixed(2).replace('.', ',');
	}

	substratFeuchte(): string | undefined {
		// Live berechnen statt gespeicherten Wert lesen
		const v = this.es.evaluate('Substratfeuchte') as string | undefined;
		if (v === undefined) return undefined;
		// Wert auch im Dokument aktualisieren für Speicherung
		this.data.header.substratFeuchte = v;
		return v;
	}

	// --- Zuletzt/häufig verwendete Substrate + Horizonte ---

	override ngOnInit() {
		super.ngOnInit();
		this.loadRecentSubstrate();
		this.loadRecentHorizonte();
		setTimeout(() => this.trackingEnabled = true);
	}

	/**
	 * Lazy-Init der G-Sub-Objekte (gefuege/feindurchwurzelung/probeentnahme)
	 * bei jeder Change-Detection — Two-Way-Bindings im Template brauchen
	 * existierende Properties (Angular erlaubt keine Methoden-Aufrufe in [(model)]).
	 */
	override ngDoCheck(): void {
		super.ngDoCheck();
		if (!this.data) return;
		// Humusauflage: die drei Zeilen oben in der Horizontfolge binden direkt
		// auf humusAuflage.ol/of/oh — bei älteren oder importierten Punkten fehlt
		// das Objekt.
		if (!this.data.extendedHeader.humusAuflage) this.data.extendedHeader.humusAuflage = {};
		if (!isG(this.data) || !Array.isArray(this.data.Substratfolge)) return;
		for (const row of this.data.Substratfolge) ensureSubstratRow(row);
	}

	private loadRecentSubstrate() {
		const recent = this.storage.getSync(this.STORAGE_KEY_SUBSTRAT) as string[] | undefined;
		if (recent?.length) {
			this.recentSubstrate = this.computeTop(recent);
		}
	}

	private loadRecentHorizonte() {
		const recent = this.storage.getSync(this.STORAGE_KEY_HORIZONT) as string[] | undefined;
		if (recent?.length) {
			this.recentHorizonte = this.computeTop(recent);
		}
	}

	/** Zaehlt die Haeufigkeit der Historie und liefert die Top-Werte (haeufigste zuerst). */
	private computeTop(recent: string[]): string[] {
		const counts = new Map<string, number>();
		for (const s of recent) {
			counts.set(s, (counts.get(s) ?? 0) + 1);
		}
		return [...counts.entries()]
			.sort((a, b) => b[1] - a[1])
			.slice(0, this.MAX_SUGGESTIONS)
			.map(([key]) => key);
	}

	onSubstratChanged(value: string, i: number) {
		const currentValue = this.data.Substratfolge[i]?.substrat;
		this.data.Substratfolge[i].substrat = value;
		if (!this.trackingEnabled || !value?.trim()) return;
		// Nur tracken wenn sich der Wert tatsächlich geändert hat
		// (ngOnChanges auf extraSuggestions löst erneut modelChange aus)
		if (value === currentValue) return;
		const bodenarten = this.ds.getHardcoded('Bodenarten') as string[];
		if (bodenarten?.includes(value)) {
			this.trackSubstrat(value);
		}
	}

	onHorizontChanged(value: string, i: number, j: number) {
		const currentValue = this.data.Horizontfolge[i]?.horizonte[j];
		this.data.Horizontfolge[i].horizonte[j] = value;
		if (!this.trackingEnabled || !value?.trim()) return;
		// Nur tracken wenn sich der Wert tatsächlich geändert hat
		// (ngOnChanges auf extraSuggestions löst erneut modelChange aus)
		if (value === currentValue) return;
		const horizontSymbole = this.ds.getHardcoded('HorizontSymbole') as string[];
		if (horizontSymbole?.includes(value)) {
			this.trackHorizont(value);
		}
	}

	private trackSubstrat(value: string) {
		let recent = this.storage.getSync(this.STORAGE_KEY_SUBSTRAT) as string[] ?? [];
		recent.unshift(value);
		if (recent.length > this.MAX_HISTORY) recent = recent.slice(0, this.MAX_HISTORY);
		this.storage.setSync(this.STORAGE_KEY_SUBSTRAT, recent);
		this.recentSubstrate = this.computeTop(recent);
	}

	private trackHorizont(value: string) {
		let recent = this.storage.getSync(this.STORAGE_KEY_HORIZONT) as string[] ?? [];
		recent.unshift(value);
		if (recent.length > this.MAX_HISTORY) recent = recent.slice(0, this.MAX_HISTORY);
		this.storage.setSync(this.STORAGE_KEY_HORIZONT, recent);
		this.recentHorizonte = this.computeTop(recent);
	}
}
